import React, { useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { IState } from '../../redux/redux';
import { ServerResponse } from '../../utils/Server';
import Icon, { IconType } from '../Icon/Icon';
import { connect } from 'react-redux';

export interface ISignInRequest {
  SignedIn: boolean;
  Message: string;
  RedirectTo: string;
  NotificationSent: boolean;
  NotificationId: string;
  MfaRequired?: boolean;
  MaskedEmail?: string;
  PersonId?: string;
}

interface IAuthSignResponse {
  Valid?: boolean;
  Expired?: boolean;
  Approved?: boolean;
  RedirectUrl?: string;
}
interface IAuthSignResponseApi {
  Valid?: boolean;
  Expired?: boolean;
  Approved?: boolean;
  RedirectUrl?: string;
}

interface IProps extends IState {
  inModal?: Boolean;
}

const UserLogin: React.FC<IProps> = (props) => {
  const { inModal } = props;
  const [loginUsername, setLoginUsername] = useState<string>('');
  const [loginPassword, setLoginPassword] = useState<string>('');
  const [quickSignIn, setQuickSignIn] = useState<string>('');
  const [showRecoveryForm, setShowRecoveryForm] = useState<boolean>(false);
  const [recoveryFirstName, setRecoveryFirstName] = useState<string>('');
  const [recoveryLastName, setRecoveryLastName] = useState<string>('');
  const [recoveryEmailOrUsername, setRecoveryEmailOrUsername] = useState<string>('');

  // MFA state
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaPersonId, setMfaPersonId] = useState<string>('');
  const [mfaMaskedEmail, setMfaMaskedEmail] = useState<string>('');
  const [mfaMessage, setMfaMessage] = useState<string>('');

  const handleSignIn = () => {
    // Trim username so leading/trailing whitespace does not break login (backend also LTRIM/RTRIM matches).
    let request = {
      password: loginPassword,
      username: (loginUsername ?? '').trim(),
      path: window.location.pathname,
      search: window.location.search,
    };

    if (request.password == null || request.username == null || request.password.length === 0 || request.username.length === 0) {
      if (request.username && request.username.toLowerCase().indexOf('@zfairs.com') > 0) {
      } else {
        toast.warning('Please enter your username & password!');
        return;
      }
    }

    store.server.postApi<ServerResponse<ISignInRequest>>(`../Person/SignIn`, request).then((r) => {
      if (r.Success) {
        if (r.Value.MfaRequired) {
          // Handle MFA requirement
          setMfaRequired(true);
          setMfaPersonId(r.Value.PersonId || '');
          setMfaMaskedEmail(r.Value.MaskedEmail || '');
          setMfaMessage(r.Value.Message || 'Please enter the verification code sent to your email.');
          toast.info(r.Value.Message || 'MFA verification required. Please check your email for the verification code.');
        } else if (r.Value.NotificationSent) {
          runAuthSignin(r.Value.NotificationId);
        } else {
          const data = r.Value;
          if (data.RedirectTo) {
            if (data.Message) toast.info(data.Message);
            window.location.href = data.RedirectTo;
            return;
          }
          if (data.SignedIn) {
            window.location.reload();
          } else {
            toast.info(data.Message, { autoClose: 15000 });
            setLoginPassword('');
            setQuickSignIn('');
          }
        }
      } else {
        toast.error(r.Message, { autoClose: false });
      }
    });
  };

  const handleSignInQuick = () => {
    const request = { key: quickSignIn };

    store.server.postApi<ServerResponse<ISignInRequest>>(`../Person/SignInQuick`, request).then((r) => {
      if (r.Success) {
        const data = r.Value;
        if (data.RedirectTo) {
          if (data.Message) toast.info(data.Message);
          window.location.href = data.RedirectTo;
          return;
        }
        if (data.SignedIn) {
          window.location.reload();
        } else {
          toast.info(data.Message, { autoClose: 15000 });
          setLoginPassword('');
          setQuickSignIn('');
        }
      } else {
        toast.error(r.Message, { autoClose: false });
      }
    });
  };

  const handleSignInQuickEnterPress = (e) => {
    if ((e.key === 'Enter' || e.key === 'NumpadEnter') && e.shiftKey === false) {
      e.preventDefault();
      handleSignInQuick();
    }
  }

  const onLoginEnterPress = (e) => {
    if ((e.key === 'Enter' || e.key === 'NumpadEnter') && e.shiftKey === false) {
      e.preventDefault();
      handleSignIn();
    }
  };

  const handleRecoverySubmit = () => {
    // Validate required fields
    if (!recoveryFirstName || !recoveryLastName || !recoveryEmailOrUsername) {
      toast.warning('Please provide your first name, last name, and email or username.');
      return;
    }

    const request = {
      firstName: recoveryFirstName.trim(),
      lastName: recoveryLastName.trim(),
      email: recoveryEmailOrUsername.trim(),
    };

    store.server.postApi<ServerResponse<any>>(`../Person/RecoverSignIn`, request).then((r) => {
      if (r.Success) {
        toast.success('Recovery information submitted successfully. Please check your email for further instructions.');
        // Reset form
        setRecoveryFirstName('');
        setRecoveryLastName('');
        setRecoveryEmailOrUsername('');
        setShowRecoveryForm(false);
      } else {
        toast.error(r.Message || 'Failed to submit recovery request.', { autoClose: false });
      }
    });
  };

  const handleVerifyMfa = () => {
    // Validate MFA code
    if (!mfaCode || mfaCode.length !== 8) {
      toast.warning('Please enter the 8-digit verification code.');
      return;
    }

    const request = {
      personId: mfaPersonId,
      code: mfaCode,
      path: window.location.pathname,
      search: window.location.search,
    };

    store.server.postApi<ServerResponse<ISignInRequest>>(`../Person/VerifyMfaCode`, request).then((r) => {
      if (r.Success) {
        const data = r.Value;
        if (data.RedirectTo) {
          if (data.Message) toast.info(data.Message);
          window.location.href = data.RedirectTo;
          return;
        }
        if (data.SignedIn) {
          toast.success('Verification successful! Signing you in...');
          window.location.reload();
        } else {
          toast.error(data.Message || 'Verification failed. Please try again.', { autoClose: 15000 });
          setMfaCode('');
        }
      } else {
        toast.error(r.Message || 'Verification failed. Please try again.', { autoClose: false });
        setMfaCode('');
      }
    });
  };

  const handleResendMfa = () => {
    const request = {
      personId: mfaPersonId,
    };

    store.server.postApi<ServerResponse<any>>(`../Person/ResendMfaCode`, request).then((r) => {
      if (r.Success) {
        toast.success('A new verification code has been sent to your email.');
        setMfaCode('');
      } else {
        toast.error(r.Message || 'Failed to resend verification code.', { autoClose: false });
      }
    });
  };

  const onRecoveryEnterPress = (e) => {
    if ((e.key === 'Enter' || e.key === 'NumpadEnter') && e.shiftKey === false) {
      e.preventDefault();
      handleRecoverySubmit();
    }
  };

  const onMfaEnterPress = (e) => {
    if ((e.key === 'Enter' || e.key === 'NumpadEnter') && e.shiftKey === false) {
      e.preventDefault();
      handleVerifyMfa();
    }
  };

  const handleShowRecoveryForm = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowRecoveryForm(true);
  };

  const handleCancelRecovery = () => {
    setShowRecoveryForm(false);
    setRecoveryFirstName('');
    setRecoveryLastName('');
    setRecoveryEmailOrUsername('');
  };

  const runAuthSignin = (id: string) => {
    toast.dismiss();
    toast.dark('Authenticating... push notification has been set.');
    store.server.getApi<ServerResponse<IAuthSignResponseApi>>(`../api/notification/status?actionId=${id}`).then((x) => {
      if (x.Success) {
        if (x.Value.Approved) {
          toast.success('Approved... Signing in now...');
          store.server.getApi<ServerResponse<IAuthSignResponse>>(`../Person/AuthSignIn/${id}`).then((xx) => {
            if (xx.Success) {
              if (xx.Value.Approved) {
                toast.success('Approved');
                window.location.reload();
              } else if (xx.Value.Expired) {
                toast.error('Authorization Request Expired');
              } else if (xx.Value.Valid) {
                //try again...
                runAuthSignin(id);
              } else {
                toast.error('Authorization request not valid.');
              }
            }
          });
        } else if (x.Value.Expired) {
          toast.error('Authorization Request Expired');
        } else if (x.Value.Valid) {
          //try again...
          runAuthSignin(id);
        } else {
          toast.error('Authorization request not valid.');
        }
      }
    });
  };

  return (
    <div>
      {props.Settings && (
        <>
          {!inModal && <h2>{props.Settings.text.NavLogin}</h2>}
          {mfaRequired ? (
            <div className="form-horizontal">
              <h3>Two-Factor Authentication Required</h3>
              <p>{mfaMessage}</p>
              {mfaMaskedEmail && (
                <p className="text-muted">
                  A verification code has been sent to: <strong>{mfaMaskedEmail}</strong>
                </p>
              )}

              <div className="form-group">
                <label htmlFor={'mfa-code'} className="control-label col-sm-4">
                  Verification Code *
                </label>
                <div className="col-sm-7">
                  <input
                    type="text"
                    id="mfa-code"
                    maxLength={8}
                    placeholder="Enter 8-character code"
                    autoFocus={true}
                    className="form-control"
                    onKeyDown={onMfaEnterPress}
                    value={mfaCode}
                    onChange={(e) => {
                      setMfaCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''));
                    }}></input>
                </div>
              </div>

              <div className="form-group">
                <div className="col-sm-offset-4 col-sm-8 flex-between">
                  <div className="btn-group">
                    <button type="button" className="btn btn-secondary" onClick={handleVerifyMfa}>
                      <Icon type={IconType.signIn} />
                      Verify Code
                    </button>
                    <button type="button" className="btn btn-default" onClick={handleResendMfa}>
                      <Icon type={IconType.mail} />
                      Resend Code
                    </button>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => {
                        setMfaRequired(false);
                        setMfaCode('');
                        setMfaPersonId('');
                        setMfaMaskedEmail('');
                        setMfaMessage('');
                        setLoginPassword('');
                      }}>
                      ← Back to Login
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : !showRecoveryForm ? (
            <div className="form-horizontal">
              <div className="form-group">
                <label className="control-label col-sm-4" htmlFor={'login-username'}>Username</label>
                <div className="col-sm-6">
                <input
                  type="text"
                  id="login-username"
                  className="form-control"
                  maxLength={50}
                  onKeyDown={onLoginEnterPress}
                  autoFocus={true}
                  value={loginUsername}
                  onChange={(e) => {
                    setLoginUsername(e.target.value);
                  }} />
              </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-4" htmlFor={'login-password'}>Password</label>
                <div className="col-sm-6">
                <input
                  type="password"
                  id="login-password"
                  className="form-control"
                  maxLength={50}
                  onKeyDown={onLoginEnterPress}
                  value={loginPassword}
                  onChange={(e) => {
                    setLoginPassword(e.target.value);
                  }}/>
                </div>
              </div>
              <div className="form-group">
                <div className="col-sm-6 col-sm-offset-4 flex-between">
                  <button type="button" className="btn btn-secondary login-form-button" onClick={handleSignIn}>
                    <Icon type={IconType.signIn} />
                    {props.Settings.text.NavLogin}
                  </button>
                  {props.SettingsResponse && (
                    <button type="button" className="btn btn-link" onClick={handleShowRecoveryForm} title="recover username and/or password">
                      {props.Settings.text.NavLogin} help
                    </button>
                  )}
                </div>
              </div>
              {(props.Settings.LoginWithEnabled || props.Settings.LoginWithClassLink) && (
                <div className="btn-group">
                  {props.Settings.LoginWithClassLink && (
                    <>
                        <a target="_blank" rel="noreferrer" href={StoreX.BuildUrlWithFair(`/Auth/Login?providerName=ClassLink&oathType=REGISTER`)} className="btn btn-default btn-sm">
                          <i className="fa-sharp-duotone fa-solid fa-link-simple"></i>&nbsp;ClassLink
                        </a>
                    </>
                  )}
                  {props.Settings.LoginWithEnabled && (
                    <>
                        <a target="_blank" rel="noreferrer"  href={StoreX.BuildUrlWithFair(`/Auth/Login?providerName=Google&oathType=REGISTER`)} className="btn btn-default btn-sm">
                          <i className="fa-brands fa-google"></i>&nbsp;Google
                        </a>
                        <a target="_blank" rel="noreferrer" href={StoreX.BuildUrlWithFair(`/Auth/Login?providerName=Facebook&oathType=REGISTER`)} className="btn btn-default btn-sm">
                          <i className="fa-brands fa-facebook"></i>&nbsp;Facebook
                        </a>
                        <a target="_blank" rel="noreferrer" href={StoreX.BuildUrlWithFair(`/Auth/Login?providerName=WindowsLive&oathType=REGISTER`)} className="btn btn-default btn-sm">
                          <i className="fa-brands fa-windows"></i>&nbsp;Windows
                        </a>
                    </>
                  )}
                </div>
              )}

              {props.Settings.showQuickSignInOption && (
                <div className="">
                  <hr />
                  <strong>Quick Sign-In</strong>
                  <p>Sign-In using your quick access key</p>
                  <div className="form-horizontal">
                    <div className="form-group">
                      <label htmlFor={'quick-sign-in'} className="col-sm-4 control-label">
                        Key
                      </label>
                      <div className="col-sm-6">
                      <input
                        type="text"
                        id="quick-sign-in"
                        placeholder="xxx-xxx-xxx"
                  className="form-control"
                        maxLength={50}
                        value={quickSignIn}
                        onKeyDown={handleSignInQuickEnterPress}
                        onChange={(e) => {
                          setQuickSignIn(e.target.value);
                        }}/>
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="col-sm-6 col-sm-offset-4">
                        <button type="button" className="btn btn-default login-form-button" onClick={handleSignInQuick}>
                          <Icon type={IconType.magic} />
                          Sign-In Using Key
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="form-horizontal">
              <h3>Recover Your Sign-in Credentials</h3>
              <p>Please provide the following information to recover your username and/or password:</p>

              <div className="form-group">
                <label htmlFor={'recovery-first-name'} className="col-sm-4 control-label">First Name *</label>
                <div className="col-sm-6">
                <input
                  type="text"
                  id="recovery-first-name"
                  className="form-control"
                  maxLength={50}
                  autoFocus={true}
                  value={recoveryFirstName}
                  onChange={(e) => {
                    setRecoveryFirstName(e.target.value);
                  }}></input>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor={'recovery-last-name'} className="col-sm-4 control-label">Last Name *</label>
                <div className="col-sm-6">
                <input
                  type="text"
                  id="recovery-last-name"
                  className="form-control"
                  maxLength={50}
                  value={recoveryLastName}
                  onChange={(e) => {
                    setRecoveryLastName(e.target.value);
                  }}></input>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor={'recovery-email-username'}  className="col-sm-4 control-label">Email or Username *</label>
                <div className="col-sm-6">
                <input
                  type="text"
                  id="recovery-email-username"
                  className="form-control"
                  maxLength={100}
                  onKeyDown={onRecoveryEnterPress}
                  value={recoveryEmailOrUsername}
                  onChange={(e) => {
                    setRecoveryEmailOrUsername(e.target.value);
                  }}></input>
                </div>
              </div>

              <div className="form-group">
                <div className="col-sm-6 col-sm-offset-4 btn-group">
                  <button type="button" className="btn btn-secondary" onClick={handleRecoverySubmit}>
                    <Icon type={IconType.mail} />
                    Submit
                  </button>
                  <button type="button" className="btn btn-default" onClick={handleCancelRecovery}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return { ...state.main };
};

//export default ReduxMap(UserLogin);
export default connect(mapStateToProps)(UserLogin);
